import { Author } from '@natu/ui/Author';
import { DynamicDecorator } from '@natu/ui/Decorators/DynamicDecorator';
import { Typography } from '@natu/ui/Typography';

import { StoryblokRichtext, type StoryblokRichTextData } from '../../../../richtext';
import {
  type BlokItem,
  DynamicRender,
  getAssetFromStoryblok,
  resolveStoryblokStyles,
  sbEditable,
  type SBProps,
  type Spacing,
  type StoryblokAsset,
} from '../../../../utils';

interface SBDoWeFitTogetherSectionProps {
  mtMobile?: Spacing;
  mtTablet?: Spacing;
  mtDesktop?: Spacing;
  mbMobile?: Spacing;
  mbTablet?: Spacing;
  mbDesktop?: Spacing;
  heading?: string;
  subheading?: string;
  paragraphs?: StoryblokRichTextData;
  portrait?: StoryblokAsset;
  name?: string;
  position?: string;
  opinion?: string;
  cards: BlokItem[];
}

export const SBDoWeFitTogetherSection = ({ blok }: SBProps<SBDoWeFitTogetherSectionProps>) => {
  const {
    mbMobile,
    mbTablet,
    mbDesktop,
    mtMobile,
    mtTablet,
    mtDesktop,
    heading,
    subheading,
    paragraphs,
    cards,
    name,
    opinion,
    portrait,
    position,
  } = blok;

  const className = resolveStoryblokStyles({
    mt: mtMobile,
    mtTablet,
    mtDesktop,
    mb: mbMobile,
    mbTablet,
    mbDesktop,
  });

  const image = getAssetFromStoryblok(portrait, { type: 'image' });

  return (
    <div className={className} {...sbEditable(blok)}>
      <div className="mb-16 flex flex-col gap-y-14 lg:grid lg:grid-cols-[auto_511px] lg:gap-x-14 xl:gap-x-28">
        <div>
          {heading ? (
            <Typography
              component="p"
              className="font-permanent-marker text-primary-600 mb-3 xl:mb-6"
              variant={['display-xs', 'display-xs', 'display-sm']}
            >
              {heading}
            </Typography>
          ) : null}
          {subheading ? (
            <Typography
              component="h2"
              className="mb-4 font-semibold xl:mb-12"
              variant={['display-sm-28', 'display-sm-28', 'display-lg-46']}
            >
              {subheading}
            </Typography>
          ) : null}
          {paragraphs ? (
            <StoryblokRichtext className="text-lg text-gray-600" data={paragraphs} />
          ) : null}
        </div>
        {name || position || opinion ? (
          <div className="shadow-3xl relative z-10 h-fit rounded-xl bg-transparent bg-white p-6 pb-8 lg:w-10/12">
            <div className="flex flex-col gap-y-6">
              <Author image={image} name={name} role={position} variant="careersSmall" />
              {opinion ? (
                <p className="text-naturaily-vulcan text-lg xl:text-2xl">{opinion}</p>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
      <div className="relative pb-16 lg:pb-0">
        <div className="bg-primary-600 absolute -right-4 -top-52 h-52 w-52 rounded-full opacity-25 blur-[40px] md:-right-6 lg:-top-80 lg:right-12 lg:h-64 lg:w-64 lg:blur-[130px]" />
        {cards.length > 0 ? (
          <div className="grid grid-cols-1 gap-y-8 md:grid-cols-2 md:gap-x-8 lg:grid-cols-3 lg:gap-y-16 [&>*]:p-0">
            <DynamicRender data={cards} />
          </div>
        ) : null}
        <div className="absolute bottom-0 right-0 flex w-fit items-center">
          <DynamicDecorator
            decorator="circle"
            className="text-primary-600 h-8 w-8 lg:h-12 lg:w-12"
          />
          <DynamicDecorator
            decorator="quadrant"
            className="text-naturaily-airborne h-8 w-8 -rotate-90 lg:h-12 lg:w-12"
          />
        </div>
      </div>
    </div>
  );
};
